import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useParams } from 'react-router-dom';
import { visibleSections, type InstanceStatus, type Group } from '@gm-binder/shared';
import { useRunStore } from '../../store/runStore';
import { IconButton, LoadingState, Modal, Select, DiceRoller, EmptyState, Button } from '../ui';
import { BgmPanel } from '../common/BgmPanel';
import { SearchPalette } from '../common/SearchPalette';
import { SchedulingModal } from '../common/SchedulingModal';
import { RosterModal } from '../common/RosterModal';
import { CloseDayModal } from '../common/CloseDayModal';
import { RulesAssistant } from '../common/RulesAssistant';
import { clearDisplay } from '../../lib/display';
import { RefNavProvider } from '../../lib/refNav';
import { useUiStore } from '../../store/uiStore';
import { api } from '../../api/client';
import { groupAccent } from '../../lib/groups';
import styles from './BinderLayout.module.css';

const STATUS_LABELS: Record<InstanceStatus, string> = {
  active: 'In corso',
  paused: 'In pausa',
  completed: 'Conclusa',
};

/** The running-binder frame: section nav, topbar tools, content outlet, BGM panel. */
export function BinderLayout() {
  const { instanceId = '' } = useParams();
  const load = useRunStore((s) => s.load);
  const reset = useRunStore((s) => s.reset);
  const adventure = useRunStore((s) => s.adventure);
  const instance = useRunStore((s) => s.instance);
  const loading = useRunStore((s) => s.loading);
  const saving = useRunStore((s) => s.saving);
  const error = useRunStore((s) => s.error);
  const setStatus = useRunStore((s) => s.setStatus);
  const toast = useUiStore((s) => s.toast);

  const [diceOpen, setDiceOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [tool, setTool] = useState<null | 'schedule' | 'roster' | 'close'>(null);
  const [group, setGroup] = useState<Group | null>(null);

  const groupId = instance?.groupId;
  useEffect(() => {
    if (!groupId) {
      setGroup(null);
      return;
    }
    let alive = true;
    void api
      .getGroup(groupId)
      .then((g) => alive && setGroup(g))
      .catch(() => alive && setGroup(null));
    return () => {
      alive = false;
    };
  }, [groupId]);

  useEffect(() => {
    void load(instanceId);
  }, [instanceId, load]);

  useEffect(() => () => reset(), [reset]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  if (loading) return <LoadingState label="Carico la sessione…" />;
  if (error || !adventure || !instance) {
    return (
      <EmptyState
        icon="⚠️"
        title="Sessione non trovata"
        description={error ?? 'Questa sessione non esiste più.'}
        actions={
          <Link to="/runs">
            <Button variant="primary">Torna alle sessioni</Button>
          </Link>
        }
      />
    );
  }

  const sections = visibleSections(adventure.sections);

  return (
    <div className={styles.layout}>
      <header className={styles.topbar}>
        <span className={styles.back}>
          <Link to={`/library/${adventure.slug}`}>
            <IconButton label="Torna all'avventura">←</IconButton>
          </Link>
        </span>
        <div className={styles.titles}>
          <span className={styles.advTitle}>{adventure.title}</span>
          <span className={styles.runMeta}>
            <span className={styles.runName}>{instance.name}</span>
            {group && (
              <Link
                to={`/groups/${group.id}`}
                className={styles.groupChip}
                style={{ ['--accent' as string]: groupAccent(group.color) }}
                title={`Gruppo: ${group.name}`}
              >
                <span className={styles.groupSeal} aria-hidden />
                {group.name}
              </Link>
            )}
          </span>
        </div>

        <div className={styles.tools}>
          <span className={styles.saving}>{saving ? 'Salvataggio…' : 'Salvato ✓'}</span>
          <Select
            className={styles.statusSelect}
            value={instance.status}
            onChange={(e) => void setStatus(e.target.value as InstanceStatus)}
            aria-label="Stato sessione"
          >
            {(Object.keys(STATUS_LABELS) as InstanceStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
          <IconButton label="Lancia dadi" onClick={() => setDiceOpen(true)}>
            🎲
          </IconButton>
          <IconButton label="Pianifica prossima sessione" onClick={() => setTool('schedule')}>
            📅
          </IconButton>
          <IconButton label="Giocatori e personaggi" onClick={() => setTool('roster')}>
            👥
          </IconButton>
          <IconButton label="Chiudi la giornata" onClick={() => setTool('close')}>
            🌙
          </IconButton>
          <IconButton label="Cerca (Ctrl+K)" onClick={() => setSearchOpen(true)}>
            🔎
          </IconButton>
          <IconButton
            label="Apri display giocatori"
            onClick={() => window.open('/display', 'gmbinder-display', 'width=1280,height=720')}
          >
            📺
          </IconButton>
          <IconButton
            label="Pulisci display"
            onClick={() => {
              clearDisplay(instance.id);
              toast('Display pulito', 'info');
            }}
          >
            🌑
          </IconButton>
        </div>
      </header>

      <nav className={styles.sidebar}>
        {sections.map((section) => (
          <NavLink
            key={section.id}
            to={`/run/${instance.id}/${section.id}`}
            className={({ isActive }) =>
              [styles.navItem, isActive ? styles.navActive : undefined].filter(Boolean).join(' ')
            }
          >
            <span className={styles.navIcon} aria-hidden>
              {section.icon}
            </span>
            {section.title}
          </NavLink>
        ))}
      </nav>

      <RefNavProvider adventure={adventure} instanceId={instance.id}>
        <main className={styles.content}>
          <div className={styles.contentInner}>
            <Outlet />
          </div>
        </main>
      </RefNavProvider>

      <div className={styles.bgm}>
        <BgmPanel slug={adventure.slug} />
      </div>

      <Modal open={diceOpen} onClose={() => setDiceOpen(false)} title="🎲 Lancio Dadi">
        <DiceRoller />
      </Modal>
      <SearchPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        adventure={adventure}
        instanceId={instance.id}
      />
      {tool === 'schedule' && <SchedulingModal instance={instance} onClose={() => setTool(null)} />}
      {tool === 'roster' && (
        <RosterModal instance={instance} adventure={adventure} onClose={() => setTool(null)} />
      )}
      {tool === 'close' && <CloseDayModal instance={instance} onClose={() => setTool(null)} />}

      <RulesAssistant />
    </div>
  );
}
